import type { HeliosFunctionCallRecordValue, HeliosRecordDataTablesData, HeliosRecordFunction } from 'types/dataHelios';
import type { runHeliosHegFunctionReq, runHeliosHegWorkflowFunctionReq } from 'types/helios/runFunctions';
import type { TemplateFunctionRes, TemplateTypeDataType } from 'types/templates';
import { getRowId } from 'components/common/Table/tableFunctions';
import { uniq } from 'lodash';

export type HeliosFunctionContext = {
  function: HeliosRecordFunction;
  functionData?: TemplateFunctionRes;
  rowId?: string;
  recordValues?: HeliosFunctionCallRecordValue[];
};

// Mirrors backend ETemplateDataType (TemplateTypeDataType union); unknown values fall back to 'String'.
const BE_TEMPLATE_DATA_TYPES: ReadonlySet<TemplateTypeDataType> = new Set([
  'Int32',
  'Int64',
  'Decimal',
  'String',
  'DateTime',
  'Boolean',
  'GuidArray',
  'Base64',
]);

const isTemplateDataType = (dataType?: string | null): dataType is TemplateTypeDataType =>
  BE_TEMPLATE_DATA_TYPES.has(dataType as TemplateTypeDataType);

export const buildFunctionCallRecordValues = (
  items?: { columnKey: string; columnName?: string | null; dataType?: string | null; rawValue?: unknown }[]
): HeliosFunctionCallRecordValue[] | undefined =>
  items?.map((item) => ({
    columnKey: item.columnKey,
    columnName: item.columnName ?? item.columnKey,
    dataType: isTemplateDataType(item.dataType) ? item.dataType : 'String',
    value:
      item.rawValue != null && typeof item.rawValue === 'object'
        ? String((item.rawValue as { id?: unknown })?.id ?? '')
        : ((item.rawValue as string | number | boolean | null) ?? null),
  }));

/**
 * Lazy record-values resolver — evaluated at function-invocation time, not at render.
 * getFallbackRowId supplies a row for header-level invocations (e.g. a sole selected row);
 * ambiguous selections must return undefined so record-dependent conditions are skipped.
 */
export const buildRecordValuesGetter =
  (recordsByRecordNumber: Record<string, HeliosRecordDataTablesData> | undefined, getFallbackRowId?: () => string | undefined) =>
  (rowId?: string): HeliosFunctionCallRecordValue[] | undefined => {
    const effectiveRowId = rowId || getFallbackRowId?.();
    return buildFunctionCallRecordValues(effectiveRowId ? recordsByRecordNumber?.[effectiveRowId]?.items : undefined);
  };

type BuildPayloadArgs = {
  context: HeliosFunctionContext;
  request?: runHeliosHegFunctionReq | runHeliosHegWorkflowFunctionReq;
  datasourceId: string;
};

const buildColumnValues = (rowItems: HeliosRecordDataTablesData['items'] | undefined) => {
  if (!rowItems) return undefined;
  return Object.fromEntries(rowItems.map((item) => [item.columnKey, item.rawValue]));
};

export const buildListingFunctionPayload = ({
  context,
  request: functionData,
  datasourceId,
  recordsByRecordNumber,
  rowDataByIdentifier,
  visibleSelectedRows,
}: BuildPayloadArgs & {
  recordsByRecordNumber: Record<string, HeliosRecordDataTablesData> | undefined;
  rowDataByIdentifier: Record<string, HeliosRecordDataTablesData>;
  visibleSelectedRows: string[];
}): runHeliosHegFunctionReq | runHeliosHegWorkflowFunctionReq => {
  const isWorkflow = context.function?.functionType === 'HegWorkflow';
  const isCommon = context.function?.isCommon;
  const singleRowId = context.rowId;
  const functionId = context.function?.id;

  const recordsWf =
    !isCommon && singleRowId
      ? [Number(singleRowId)]
      : !isCommon && visibleSelectedRows.length
        ? visibleSelectedRows.map((row) => Number(getRowId(row)))
        : undefined;

  const records =
    !isCommon && singleRowId
      ? [
          {
            recordNumber: Number(singleRowId),
            masterRecordNumber: recordsByRecordNumber?.[singleRowId]?.masterRecordNumber,
            columnValues: buildColumnValues(recordsByRecordNumber?.[singleRowId]?.items),
          },
        ]
      : !isCommon && visibleSelectedRows.length
        ? visibleSelectedRows.map((row) => {
            const rowData = rowDataByIdentifier[row];
            return {
              recordNumber: rowData?.recordNumber ?? Number(getRowId(row)),
              masterRecordNumber: rowData?.masterRecordNumber,
              columnValues: buildColumnValues(rowData?.items),
            };
          })
        : undefined;

  switch (true) {
    case isWorkflow:
      return {
        actionId: functionId!,
        datasourceId,
        comment: (functionData as runHeliosHegWorkflowFunctionReq)?.comment ?? undefined,
        recordNumbers: recordsWf,
      };
    case Boolean(functionData) && isCommon:
      return functionData as runHeliosHegFunctionReq;
    case Boolean(!functionData) && isCommon:
      return {
        heliosFunctionId: context.function?.id,
        datasourceId,
      };
    case Boolean(functionData) && !isCommon:
      return { ...(functionData as runHeliosHegFunctionReq), records };
    case Boolean(!functionData) && !isCommon:
      return { heliosFunctionId: context.function?.id, datasourceId, records };
    default:
      throw new Error('Invalid function type');
  }
};

export const buildDetailTableFunctionPayload = ({
  context,
  request: functionData,
  datasourceId,
  selectedRows,
}: BuildPayloadArgs & {
  selectedRows: string[];
}): runHeliosHegFunctionReq | runHeliosHegWorkflowFunctionReq => {
  const isWorkflow = context.function?.functionType === 'HegWorkflow';
  const isCommon = context.function?.isCommon;
  const singleRowId = context.rowId;
  const functionId = context.function?.id;

  const recordNumbers = uniq(
    !isCommon && singleRowId ? [Number(singleRowId)] : !isCommon && selectedRows.length ? selectedRows.map((row) => Number(getRowId(row))) : undefined
  );

  switch (true) {
    case isWorkflow:
      return {
        actionId: functionId!,
        datasourceId,
        comment: (functionData as runHeliosHegWorkflowFunctionReq)?.comment ?? undefined,
        recordNumbers,
      };
    case Boolean(functionData) && isCommon:
      return functionData as runHeliosHegFunctionReq;
    case Boolean(!functionData) && isCommon:
      return {
        heliosFunctionId: context.function?.id,
        datasourceId,
      };
    case Boolean(functionData) && !isCommon:
      return { ...(functionData as runHeliosHegFunctionReq), recordNumbers } as runHeliosHegFunctionReq & { recordNumbers?: number[] };
    case Boolean(!functionData) && !isCommon:
      return { heliosFunctionId: context.function?.id, datasourceId, recordNumbers } as runHeliosHegFunctionReq & {
        recordNumbers?: number[];
      };
    default:
      throw new Error('Invalid function type');
  }
};

export const buildDetailRecordFunctionPayload = ({
  context,
  request: functionData,
  datasourceId,
  recordId,
  masterRecordId,
  getColumnValues,
}: BuildPayloadArgs & {
  recordId: string;
  masterRecordId: string;
  getColumnValues: () => Record<string, string | null>;
}): runHeliosHegFunctionReq | runHeliosHegWorkflowFunctionReq => {
  const isWorkflow = context.function?.functionType === 'HegWorkflow';
  const functionId = context.function?.id;

  if (isWorkflow) {
    return {
      actionId: functionId!,
      datasourceId,
      comment: (functionData as runHeliosHegWorkflowFunctionReq)?.comment ?? undefined,
      recordNumbers: [Number(recordId)],
    };
  }

  const records = [
    {
      recordNumber: Number(recordId),
      masterRecordNumber: Number(masterRecordId),
      columnValues: getColumnValues(),
    },
  ];

  if (functionData) {
    return { ...(functionData as runHeliosHegFunctionReq), records };
  }

  return {
    heliosFunctionId: functionId!,
    datasourceId,
    records,
  };
};
